import { Menu, MenuItem, Scrollable } from '@pique/component';
import type { Tag } from '@pique/env/filter';
import { useService, WorkspaceService } from '@toeverything/infra';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';

import * as styles from './multi-select.css';

export const TagMultiSelect = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) => {
  const workspace = useService(WorkspaceService).workspace;
  const tags: Tag[] = workspace.docCollection.meta.properties.tags?.options ?? [];

  const selectedTags = useMemo(
    () => tags.filter(t => value.includes(t.id)),
    [tags, value]
  );

  const handleToggle = useCallback(
    (tag: Tag, e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const isSelected = value.includes(tag.id);
      if (isSelected) {
        onChange(value.filter(v => v !== tag.id));
      } else {
        onChange([...value, tag.id]);
      }
    },
    [onChange, value]
  );

  const displayText = selectedTags.length
    ? selectedTags.map(t => t.value).join(', ')
    : 'Select tags';

  const items = (
    <Scrollable.Root>
      <Scrollable.Viewport className={styles.optionList}>
        {tags.length === 0 ? (
          <MenuItem disabled>No tags found</MenuItem>
        ) : (
          tags.map(tag => {
            const isSelected = value.includes(tag.id);
            return (
              <MenuItem
                key={tag.id}
                checked={isSelected}
                onClick={(e: MouseEvent) => handleToggle(tag, e)}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: tag.color,
                    flexShrink: 0,
                  }}
                />
                <span>{tag.value}</span>
              </MenuItem>
            );
          })
        )}
      </Scrollable.Viewport>
      <Scrollable.Scrollbar className={styles.scrollbar} />
    </Scrollable.Root>
  );

  return (
    <Menu items={items}>
      <div className={styles.content} style={{ cursor: 'pointer' }}>
        {selectedTags.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {selectedTags.slice(0, 3).map(tag => (
              <div
                key={tag.id}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: tag.color,
                  flexShrink: 0,
                }}
              />
            ))}
            <span className={styles.text}>{displayText}</span>
          </div>
        ) : (
          <span className={clsx(styles.text, 'empty')}>{displayText}</span>
        )}
      </div>
    </Menu>
  );
};
